import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormHelperText, Grid, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import _ from 'lodash';
import '../../../../../../../node_modules/video-react/dist/video-react.css';
import { Player } from 'video-react';

import { Button, SubmitButton } from '../../../common/button';
import FileInput from '../../../common/file_input';
import { isNullOrUndefined } from '../../../../core/utils/misc_utils';
import { SelectFieldFormik, SelectFieldMultipleFormikWithMore } from '../../../common/select_field';
import { getAllPodsState } from '../../../../features/pod/get_all_pods';
import { createAdvertisementsAction, createAdvertisementsState } from '../../../../features/pod/create_advertisements';
import useNotification from '../../../../core/hooks/use_notification';
import RadioGroupFormik from '../../../common/radio_group';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import { createAdvertisementsNotificationData, getAllProductProductsNotificationData } from '../../../../core/utils/notification_utils';
import { yesNoOptions } from '../../../common/form_utils';
import { DateTimePickerFormik } from '../../../common/date_picker';
import { parseDateString } from '../../../../core/utils/date_utils';
import { MISC_DATE_ISO_FORMAT } from '../../../../core/constants';
import { getAllProductsAction } from '../../../../features/product/get_all_products';
import { AutocompleteMultipleSearchOnTypeFormik } from '../../../common/autocomplete';
import { AdvertisementFileTypeEnum } from '../../../../core/enums/pod/advertisement';
import SectionPaper from '../../../common/section_paper';

const AdvertisementsFormDialog = ({ podId, advertisementCount, refreshList }) => {
    const { showNotification, showErrorMessage } = useNotification();
    const dispatch = useDispatch();

    const getAllPods = useSelector(getAllPodsState);
    const createAdvertisements = useSelector(createAdvertisementsState);

    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [productsOptions, setProductsOptions] = useState([]);
    const [loadingProductsOptions, setLoadingProductsOptions] = useState(false);

    const mapProductsOptions = useCallback((data) => {
        return data?.map(product => ({
            key: product?.id + Math.random(),
            value: product?.id,
            label: product?.name
        }));
    }, []);

    const orderOptions = useCallback(() => {
        if (_.isNumber(advertisementCount)) {
            const result = _.times(advertisementCount, num => num + 1);
            result.push(advertisementCount + 1);
            return result?.map(order => ({ key: order, value: order, label: order }));
        }
        return [
            { key: 1, value: 1, label: 'Pierwszy' },
            { key: -1, value: -1, label: 'Ostatni' }
        ];
    }, [advertisementCount]);

    const initialValues = {
        podIds: !!podId ? [podId] : [],
        displayOrder: !podId ? 1 : '',
        withDates: false,
        displayTimeFrom: '',
        displayTimeTo: '',
        products: [],
        file: null
    };

    const schema = Yup.object().shape({
        displayOrder: Yup.number().required(),
        podIds: Yup.array().min(1),
        displayTimeFrom: Yup.date().when('withDates', {
            is: true,
            then: Yup.date().transform(parseDateString).nullable().required(),
            otherwise: Yup.date().transform(parseDateString).nullable().notRequired()
        }),
        displayTimeTo: Yup.date().when('withDates', {
            is: true,
            then: Yup.date().transform(parseDateString).nullable().required(),
            otherwise: Yup.date().transform(parseDateString).nullable().notRequired()
        }),
        file: Yup.mixed().required()
    });

    const onOpen = () => {
        setOpen(true);
        setProductsOptions([]);
    };
    const onClose = () => {
        setOpen(false);
        setFile(null);
    };

    const onAttach = (file, formik) => {
        if (['image/png', 'image/jpeg'].includes(file.type)) {
            const image = new Image();
            image.src = URL.createObjectURL(file);
            image.onload = () => {
                if (image.height * 9 / 16 !== image.width) {
                    formik.setErrors({ file: 'Plik powinien mieć proporcje 9:16' });
                } else {
                    let reader = new FileReader();
                    reader.onloadend = () => setFile({ type: AdvertisementFileTypeEnum.IMAGE, content: reader.result });
                    reader.readAsDataURL(file);
                    formik.setFieldValue('file', file);
                }
            };
        } else {
            setFile({ type: AdvertisementFileTypeEnum.VIDEO, content: URL.createObjectURL(file) });
            formik.setFieldValue('file', file);
        }
    };

    const onProductsInputChange = (query, formik) => {
        if (query.length >= 3) {
            setProductsOptions([]);
            setLoadingProductsOptions(true);
            dispatch(getAllProductsAction({ query }))
                .then(response => {
                    showErrorMessage(getAllProductProductsNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        const newOptions = mapProductsOptions(response?.payload).concat(formik.values.products);
                        setProductsOptions([...new Map(newOptions.map(o => [o.value, o])).values()]);
                    }
                    setLoadingProductsOptions(false);
                });
        }
    };

    const onSubmit = (values) => {
        const displayTimeFrom = !values.withDates ? undefined : values?.displayTimeFrom?.format(MISC_DATE_ISO_FORMAT);
        const displayTimeTo = !values.withDates ? undefined : values?.displayTimeTo?.format(MISC_DATE_ISO_FORMAT);
        const productIds = values.products?.map(p => p?.value) || [];

        const form = {
            ..._.omit(values, ['file', 'displayTimeFrom', 'displayTimeTo', 'products', 'withDates']),
            displayTimeFrom, displayTimeTo, productIds, fileType: file?.type
        };

        dispatch(createAdvertisementsAction({ form, file: values.file }))
            .then(response => {
                showNotification(createAdvertisementsNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    onClose();
                    refreshList && refreshList();
                }
            });
    };

    return (
        <>
            <Button onClick={ onOpen }>Dodaj reklamę</Button>
            <Dialog open={ open } onClose={ onClose } fullWidth maxWidth="lg">
                <Formik
                    initialValues={ initialValues }
                    validationSchema={ schema }
                    validateOnChange={ false }
                    validateOnBlur={ false }
                    onSubmit={ onSubmit }
                >
                    { (formik) => (
                        <Form>
                            <DialogTitle sx={ { fontWeight: 600, pb: 1 } }>Nowa reklama</DialogTitle>
                            <Divider/>

                            <DialogContent sx={ { py: 3 } }>
                                <Grid container spacing={ 4 } alignItems="flex-start">
                                    <Grid item xs={ 12 } lg={ 5 }>
                                        <SectionPaper title="Podgląd pliku" sx={ { height: '100%' } }>
                                            <Stack spacing={ 3 }>
                                                <Box display="flex" justifyContent="center" alignItems="center" sx={ {
                                                    backgroundColor: 'slate.50',
                                                    border: theme => `1px dashed ${ theme.palette.slate[300] }`,
                                                    borderRadius: 2,
                                                    minHeight: 420,
                                                    p: 1
                                                } }>
                                                    { file ? (
                                                        file.type === AdvertisementFileTypeEnum.VIDEO ? (
                                                            <Player muted playsInline src={ file.content }
                                                                    fluid={ false } width={ 280 } height={ 480 }/>
                                                        ) : (
                                                            <img src={ file.content } alt="Podgląd" style={ {
                                                                maxWidth: '100%',
                                                                maxHeight: 480,
                                                                borderRadius: 4,
                                                                objectFit: 'contain'
                                                            } }/>
                                                        )
                                                    ) : (
                                                        <Typography color="text.secondary" variant="body2">Brak pliku
                                                            (Wymagany format 9:16)</Typography>
                                                    ) }
                                                </Box>
                                                <Box>
                                                    <FileInput
                                                        label="Plik reklamy"
                                                        text="Dodaj lub przeciągnij plik png/jpg/mp4"
                                                        errorMessage="Wymagany png, jpg lub mp4."
                                                        validate={ f => ['image/png', 'image/jpeg', 'video/mp4'].includes(f.type) }
                                                        isAttached={ !isNullOrUndefined(formik.values.file) }
                                                        onAttach={ f => onAttach(f, formik) }
                                                        onRemove={ () => {
                                                            formik.setFieldValue('file', null);
                                                            setFile(null);
                                                        } }
                                                    />
                                                    { formik.errors.file && <FormHelperText error sx={ {
                                                        px: 1,
                                                        mt: 1
                                                    } }>{ formik.errors.file }</FormHelperText> }
                                                </Box>
                                            </Stack>
                                        </SectionPaper>
                                    </Grid>

                                    <Grid item xs={ 12 } lg={ 7 }>
                                        <Stack spacing={ 3 }>
                                            <SectionPaper title="Lokalizacja i kolejność">
                                                <Stack spacing={ 2.5 }>
                                                    <SelectFieldMultipleFormikWithMore
                                                        size="small"
                                                        name="podIds"
                                                        label="Wybierz punkty sprzedaży (Pody) *"
                                                        defaultOptions={ getAllPods?.data?.filter(p => p?.device?.serviceId != null).map(p => ({
                                                            value: p.id,
                                                            label: `${ p.indoorPartner?.name } (${ p.description })`
                                                        })) }
                                                        extraOptions={ getAllPods?.data?.filter(p => p?.device?.serviceId == null).map(p => ({
                                                            value: p.id,
                                                            label: `${ p.indoorPartner?.name } (Brak urządzenia)`
                                                        })) }
                                                        disabled={ !!podId }
                                                    />
                                                    <Grid container>
                                                        <Grid item xs={ 12 } sm={ 6 }>
                                                            <SelectFieldFormik size="small" name="displayOrder"
                                                                               label="Kolejność wyświetlania *"
                                                                               options={ orderOptions() }/>
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                            </SectionPaper>

                                            <SectionPaper title="Czas emisji i powiązania">
                                                <Stack spacing={ 2.5 }>
                                                    <RadioGroupFormik name="withDates"
                                                                      label="Ustaw daty obowiązywania *"
                                                                      options={ yesNoOptions }/>

                                                    { formik.values.withDates && (
                                                        <Grid container spacing={ 2 }>
                                                            <Grid item xs={ 12 } sm={ 6 }>
                                                                <DateTimePickerFormik size="small"
                                                                                      name="displayTimeFrom"
                                                                                      label="Data początkowa *"/>
                                                            </Grid>
                                                            <Grid item xs={ 12 } sm={ 6 }>
                                                                <DateTimePickerFormik size="small" name="displayTimeTo"
                                                                                      label="Data końcowa *"/>
                                                            </Grid>
                                                        </Grid>
                                                    ) }

                                                    <AutocompleteMultipleSearchOnTypeFormik
                                                        size="small"
                                                        options={ productsOptions }
                                                        onInputChange={ v => onProductsInputChange(v, formik) }
                                                        name="products"
                                                        label="Uzależnij reklamę od występowania wskazanego produktu"
                                                        loading={ loadingProductsOptions }
                                                    />
                                                </Stack>
                                            </SectionPaper>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </DialogContent>

                            <Divider/>
                            <DialogActions sx={ { p: 2, px: 3, gap: 1 } }>
                                <Button variant="outlined" onClick={ onClose }>Anuluj</Button>
                                <SubmitButton isLoading={ createAdvertisements?.loading }>Zapisz reklamę</SubmitButton>
                            </DialogActions>
                        </Form>
                    ) }
                </Formik>
            </Dialog>
        </>
    );
};

export default AdvertisementsFormDialog;